import { useEffect, useState } from "react";
import { Link, useOutletContext, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, CalendarDays, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { fmtDate, Lead, STATUSES, STATUS_STYLE } from "./types";
import StatusBadge from "./StatusBadge";
import { Attachments, FollowUpCard, NotesCard } from "./LeadPanels";
import type { ShellCtx } from "./CrmShell";

export default function LeadDetail() {
  const { id } = useParams();
  const { staff } = useOutletContext<ShellCtx>();
  const [lead, setLead] = useState<Lead | null>(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    setLead(null);
    api.get(`/leads/${id}`).then((r) => setLead(r.data)).catch((e) => setErr(apiError(e)));
  }, [id]);

  const update = async (body: Record<string, unknown>, msg = "Lead updated") => {
    try {
      const { data } = await api.patch(`/leads/${id}`, body);
      setLead(data);
      toast.success(msg);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (err) return <p className="p-6 text-red-400" data-testid="lead-error">{err}</p>;
  if (!lead) return <p className="p-6 text-slate-500">Loading…</p>;

  const original = lead.related?.find((r) => r.is_original);
  const details: [string, string][] = [
    ["Company", lead.company], ["Site address", lead.address], ["Location", lead.location], ["Service", lead.work_type],
    ["Area", lead.area_sqm ? `${lead.area_sqm} m²` : ""], ["Source page", lead.source_page],
    ["Campaign", Object.entries(lead.utm || {}).map(([k, v]) => `${k}=${v}`).join(", ")], ["Received", fmtDate(lead.created_at)],
  ];

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4 md:p-6" data-testid="lead-detail">
      <Link to="/staff" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-brand-400" data-testid="lead-back-link"><ArrowLeft className="h-4 w-4" /> All leads</Link>
      {lead.duplicate_of && original && (
        <div className="flex items-center gap-2 border border-orange-500/40 bg-orange-500/10 p-3 text-sm text-orange-200" data-testid="duplicate-banner">
          <AlertTriangle className="h-4 w-4" /> Possible duplicate — matches existing lead
          <Link to={`/staff/leads/${original.id}`} className="font-semibold underline" data-testid="duplicate-original-link">{original.name} ({fmtDate(original.created_at)})</Link>
        </div>
      )}
      <div className="grid gap-4 lg:grid-cols-[1.3fr_1fr]">
        <div className="space-y-4">
          <section className="border border-slate-800 bg-[#161920] p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h1 className="text-3xl font-bold uppercase text-white" data-testid="lead-name">{lead.name}</h1>
                <div className="mt-1"><StatusBadge status={lead.status} /></div>
              </div>
              <div className="flex gap-2">
                {lead.phone && <a href={`tel:${lead.phone}`} className="btn-brand !min-h-0 !px-3 !py-2 !text-base" data-testid="lead-call-button"><Phone className="h-4 w-4" />{lead.phone}</a>}
                {lead.email && <a href={`mailto:${lead.email}`} className="btn-ghost !min-h-0 !px-3 !py-2 !text-base" data-testid="lead-email-button"><Mail className="h-4 w-4" />Email</a>}
              </div>
            </div>
            {lead.email && <p className="mt-2 text-sm text-slate-400">{lead.email}</p>}
            <dl className="mt-5 grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
              {details.filter(([, v]) => v).map(([k, v]) => (
                <div key={k}><dt className="font-mono text-[10px] uppercase text-slate-500">{k}</dt><dd className="text-slate-200">{v}</dd></div>
              ))}
            </dl>
            {lead.message && <p className="mt-5 whitespace-pre-wrap border-l-2 border-brand-500 bg-[#0F1115] p-3 text-sm text-slate-300" data-testid="lead-message">{lead.message}</p>}
            {lead.site_visit_requested && (
              <div className="mt-4 flex items-center gap-2 border border-brand-500/40 bg-brand-500/10 p-3 text-sm text-brand-200" data-testid="lead-site-visit">
                <CalendarDays className="h-4 w-4" /> Site visit requested: <strong>{new Date(lead.site_visit_date + "T00:00").toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}</strong> · {lead.site_visit_slot || "any time"}
              </div>
            )}
            <Attachments lead={lead} />
          </section>
          {!!lead.related?.length && (
            <section className="border border-slate-800 bg-[#161920] p-5" data-testid="related-leads">
              <h2 className="mb-3 font-mono text-xs uppercase text-slate-500">Linked enquiries (same contact)</h2>
              <ul className="space-y-2 text-sm">
                {lead.related.map((r) => (
                  <li key={r.id} className="flex items-center justify-between gap-2">
                    <Link to={`/staff/leads/${r.id}`} className="text-white hover:text-brand-400">{r.name} — {r.work_type}{r.is_original ? " (original)" : ""}</Link>
                    <span className="flex items-center gap-2 text-xs text-slate-500">{fmtDate(r.created_at)} <StatusBadge status={r.status} /></span>
                  </li>
                ))}
              </ul>
            </section>
          )}
          <NotesCard lead={lead} onChange={setLead} />
        </div>
        <div className="space-y-4">
          <section className="border border-slate-800 bg-[#161920] p-5">
            <h2 className="mb-3 font-mono text-xs uppercase text-slate-500">Status</h2>
            <div className="grid grid-cols-5 gap-1" data-testid="lead-status-control">
              {STATUSES.map((s) => (
                <button key={s} onClick={() => update({ status: s }, `Status: ${s}`)} data-testid={`lead-status-${s}`}
                  className={`rounded-sm border py-2 font-mono text-[11px] uppercase transition-colors ${lead.status === s ? STATUS_STYLE[s] : "border-slate-700 text-slate-400 hover:border-slate-500"}`}>
                  {s}
                </button>
              ))}
            </div>
            <h2 className="mb-2 mt-5 font-mono text-xs uppercase text-slate-500">Owner</h2>
            <select className="crm-field" value={lead.owner_id || ""} onChange={(e) => update({ owner_id: e.target.value || null }, "Owner updated")} data-testid="lead-owner-select">
              <option value="">Unassigned</option>
              {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </section>
          <FollowUpCard lead={lead} onSave={update} />
        </div>
      </div>
    </div>
  );
}
